// AUTO-GENERATED — do not hand-edit. Regenerate if the icon set changes.
// Re-exports Solar icons under their Lucide names, so call sites work unchanged
// after swapping the import source from 'lucide-react' to this module. The render
// weight comes from the nearest <SolarProvider> (default Linear); an explicit
// `weight` prop wins. Icons tagged (approx) have no exact Solar equivalent — the
// closest match was chosen.
import {
    AddCircle as SolarAddCircle,
    AltArrowDown as SolarAltArrowDown,
    AltArrowLeft as SolarAltArrowLeft,
    AltArrowRight as SolarAltArrowRight,
    AltArrowUp as SolarAltArrowUp,
    Archive as SolarArchive,
    ArrowDown as SolarArrowDown,
    ArrowLeft as SolarArrowLeft,
    ArrowRight as SolarArrowRight,
    ArrowRightUp as SolarArrowRightUp,
    ArrowUp as SolarArrowUp,
    Bell as SolarBell,
    Bill as SolarBill,
    Book as SolarBook,
    Calendar as SolarCalendar,
    CalendarAdd as SolarCalendarAdd,
    CalendarDate as SolarCalendarDate,
    CalendarMark as SolarCalendarMark,
    ChartSquare as SolarChartSquare,
    ChatSquare as SolarChatSquare,
    CheckCircle as SolarCheckCircle,
    CheckSquare as SolarCheckSquare,
    ChefHat as SolarChefHat,
    ClipboardCheck as SolarClipboardCheck,
    ClipboardList as SolarClipboardList,
    ClockCircle as SolarClockCircle,
    CloseCircle as SolarCloseCircle,
    Confetti as SolarConfetti,
    Copy as SolarCopy,
    Cup as SolarCup,
    DangerCircle as SolarDangerCircle,
    DangerTriangle as SolarDangerTriangle,
    DocumentText as SolarDocumentText,
    Donut as SolarDonut,
    Download as SolarDownload,
    Exit as SolarExit,
    Eye as SolarEye,
    EyeClosed as SolarEyeClosed,
    File as SolarFile,
    Folder as SolarFolder,
    FolderOpen as SolarFolderOpen,
    ForbiddenCircle as SolarForbiddenCircle,
    Gamepad as SolarGamepad,
    Gift as SolarGift,
    Global as SolarGlobal,
    HamburgerMenu as SolarHamburgerMenu,
    Home as SolarHome,
    type IconProps as SolarIconProps,
    type IconWeight as SolarIconWeight,
    InfoCircle as SolarInfoCircle,
    Key as SolarKey,
    Link as SolarLink,
    LinkMinimalistic as SolarLinkMinimalistic,
    List as SolarList,
    Lock as SolarLock,
    Logout as SolarLogout,
    Magnifer as SolarMagnifer,
    MapPoint as SolarMapPoint,
    Masks as SolarMasks,
    MedalStar as SolarMedalStar,
    MenuDots as SolarMenuDots,
    MoneyBag as SolarMoneyBag,
    Moon as SolarMoon,
    Pen as SolarPen,
    Pen2 as SolarPen2,
    PenNewSquare as SolarPenNewSquare,
    PieChart as SolarPieChart,
    Plain as SolarPlain,
    PlugCircle as SolarPlugCircle,
    Pulse2 as SolarPulse2,
    QuestionCircle as SolarQuestionCircle,
    Record as SolarRecord,
    Refresh as SolarRefresh,
    Scale as SolarScale,
    Settings as SolarSettings,
    Shield as SolarShield,
    ShieldCheck as SolarShieldCheck,
    SidebarMinimalistic as SolarSidebarMinimalistic,
    SmileCircle as SolarSmileCircle,
    SortVertical as SolarSortVertical,
    SquareArrowRight as SolarSquareArrowRight,
    SquareArrowRightUp as SolarSquareArrowRightUp,
    Stars as SolarStars,
    Stopwatch as SolarStopwatch,
    Suitcase as SolarSuitcase,
    Sun as SolarSun,
    Tag as SolarTag,
    TextBold as SolarTextBold,
    TextItalic as SolarTextItalic,
    TextUnderline as SolarTextUnderline,
    TrashBinMinimalistic as SolarTrashBinMinimalistic,
    Tuning as SolarTuning,
    Tuning2 as SolarTuning2,
    TuningSquare as SolarTuningSquare,
    Umbrella as SolarUmbrella,
    Upload as SolarUpload,
    User as SolarUser,
    UserCheck as SolarUserCheck,
    UserMinus as SolarUserMinus,
    UserPlus as SolarUserPlus,
    UsersGroupRounded as SolarUsersGroupRounded,
    Widget as SolarWidget,
} from '@solar-icons/react'
import SolarMaximize from '@solar-icons/react/csr/arrows-action/Maximize'
import SolarMonitor from '@solar-icons/react/csr/devices/Monitor'
import SolarStructure from '@solar-icons/react/csr/it/Structure'
// These four are missing from the package's top-level type barrel (a generation
// quirk), but the `exports`-mapped leaf paths are correctly typed — import direct.
import SolarMagniferZoomIn from '@solar-icons/react/csr/search/MagniferZoomIn'
import SolarMagniferZoomOut from '@solar-icons/react/csr/search/MagniferZoomOut'
import type { ComponentType } from 'react'

import type { IconProps } from './icon.js'

/** Lucide `IconProps` plus an optional Solar `weight` override. */
type WrappedIconProps = IconProps & { weight?: SolarIconWeight }

/**
 * Re-export Solar's context provider. Wrap a subtree in
 * `<SolarProvider value={{ weight: 'BoldDuotone' }}>` to make every icon below it
 * render in that weight. Without a provider the weight falls back to Solar's
 * default (Linear); an explicit `weight` prop on an icon always wins.
 */
export { SolarProvider } from '@solar-icons/react'

/**
 * Wrap a Solar icon so it exposes the Lucide `IconProps` surface (size, className,
 * color, …). Weight is inherited from the nearest `SolarProvider` (default Linear)
 * unless a `weight` prop is passed at the call site.
 */
const wrapIcon = (Icon: ComponentType<SolarIconProps>): ComponentType<WrappedIconProps> => {
    const Wrapped = (props: WrappedIconProps) => <Icon {...props} />
    return Wrapped
}

export const Activity = wrapIcon(SolarPulse2) // (approx)
export const AlertCircle = wrapIcon(SolarDangerCircle)
export const AlertTriangle = wrapIcon(SolarDangerTriangle)
export const Archive = wrapIcon(SolarArchive)
export const ArrowDown = wrapIcon(SolarArrowDown)
export const ArrowLeft = wrapIcon(SolarArrowLeft)
export const ArrowRight = wrapIcon(SolarArrowRight)
export const ArrowUp = wrapIcon(SolarArrowUp)
export const ArrowUpDown = wrapIcon(SolarSortVertical)
export const ArrowUpRight = wrapIcon(SolarArrowRightUp)
export const Award = wrapIcon(SolarMedalStar) // (approx)
export const Ban = wrapIcon(SolarForbiddenCircle) // (approx)
export const BarChart3 = wrapIcon(SolarChartSquare)
export const Bell = wrapIcon(SolarBell)
export const Bold = wrapIcon(SolarTextBold)
export const BookOpen = wrapIcon(SolarBook)
export const Briefcase = wrapIcon(SolarSuitcase)
export const Cake = wrapIcon(SolarGift) // (approx)
export const Calendar = wrapIcon(SolarCalendar)
export const CalendarCheck = wrapIcon(SolarCalendarMark) // (approx)
export const CalendarClock = wrapIcon(SolarCalendarDate) // (approx)
export const CalendarHeart = wrapIcon(SolarCalendarMark) // (approx)
export const CalendarX = wrapIcon(SolarCalendarDate) // (approx)
export const CalendarPlus = wrapIcon(SolarCalendarAdd)
export const CalendarRange = wrapIcon(SolarCalendar) // (approx)
export const Check = wrapIcon(SolarCheckCircle) // (approx)
export const CheckCircle = wrapIcon(SolarCheckCircle)
export const CheckSquare = wrapIcon(SolarCheckSquare)
export const ChevronDown = wrapIcon(SolarAltArrowDown)
export const ChevronLeft = wrapIcon(SolarAltArrowLeft)
export const ChevronRight = wrapIcon(SolarAltArrowRight)
export const ChevronUp = wrapIcon(SolarAltArrowUp)
export const Circle = wrapIcon(SolarRecord) // (approx)
export const CircleAlert = wrapIcon(SolarDangerCircle)
export const CircleCheck = wrapIcon(SolarCheckCircle)
export const CirclePlus = wrapIcon(SolarAddCircle)
export const ClipboardCheck = wrapIcon(SolarClipboardCheck)
export const ClipboardList = wrapIcon(SolarClipboardList)
export const Clock = wrapIcon(SolarClockCircle)
export const Coins = wrapIcon(SolarMoneyBag)
export const Construction = wrapIcon(SolarTuningSquare) // (approx)
export const Copy = wrapIcon(SolarCopy)
export const DoorOpen = wrapIcon(SolarExit) // (approx)
export const Download = wrapIcon(SolarDownload)
export const ExternalLink = wrapIcon(SolarSquareArrowRightUp) // (approx)
export const Eye = wrapIcon(SolarEye)
// Extra Solar icons with no direct Lucide-name counterpart.
export const SquareArrowRight = wrapIcon(SolarSquareArrowRight)
export const Sidebar = wrapIcon(SolarSidebarMinimalistic)
export const Donut = wrapIcon(SolarDonut)
export const HelpCircle = wrapIcon(SolarQuestionCircle)
export const EyeOff = wrapIcon(SolarEyeClosed)
export const File = wrapIcon(SolarFile)
export const FilePenLine = wrapIcon(SolarPenNewSquare) // (approx)
export const FileText = wrapIcon(SolarDocumentText)
export const Folder = wrapIcon(SolarFolder)
export const FolderKanban = wrapIcon(SolarWidget) // (approx) board/grid stand-in
export const FolderOpen = wrapIcon(SolarFolderOpen)
export const Gamepad2 = wrapIcon(SolarGamepad)
export const Globe = wrapIcon(SolarGlobal)
export const Grid3X3 = wrapIcon(SolarWidget) // (approx)
export const GripVertical = wrapIcon(SolarMenuDots) // (approx)
export const Home = wrapIcon(SolarHome)
export const Info = wrapIcon(SolarInfoCircle)
export const Italic = wrapIcon(SolarTextItalic)
export const Key = wrapIcon(SolarKey)
export const Link = wrapIcon(SolarLink)
export const Link2 = wrapIcon(SolarLinkMinimalistic)
export const List = wrapIcon(SolarList)
export const ListOrdered = wrapIcon(SolarList) // (approx)
export const Loader = wrapIcon(SolarRefresh) // (approx)
export const Lock = wrapIcon(SolarLock)
export const LogOut = wrapIcon(SolarLogout)
export const MapPin = wrapIcon(SolarMapPoint)
export const Menu = wrapIcon(SolarHamburgerMenu)
export const MessageSquarePlus = wrapIcon(SolarChatSquare) // (approx)
// Cast needed: these come from deep subpaths the lint type-service sees as untyped.
export const MagniferZoomIn = wrapIcon(SolarMagniferZoomIn as ComponentType<SolarIconProps>)
export const MagniferZoomOut = wrapIcon(SolarMagniferZoomOut as ComponentType<SolarIconProps>)
export const Maximize = wrapIcon(SolarMaximize as ComponentType<SolarIconProps>)
export const Monitor = wrapIcon(SolarMonitor as ComponentType<SolarIconProps>)
export const Moon = wrapIcon(SolarMoon)
export const MoreHorizontal = wrapIcon(SolarMenuDots)
export const Palmtree = wrapIcon(SolarUmbrella) // (approx)
export const PartyPopper = wrapIcon(SolarConfetti)
export const PenLine = wrapIcon(SolarPen)
export const Pencil = wrapIcon(SolarPen2)
export const PieChart = wrapIcon(SolarPieChart)
export const Plug = wrapIcon(SolarPlugCircle)
export const Plus = wrapIcon(SolarAddCircle) // (approx)
export const Receipt = wrapIcon(SolarBill) // (approx)
export const RefreshCcw = wrapIcon(SolarRefresh)
export const RefreshCw = wrapIcon(SolarRefresh)
export const Scale = wrapIcon(SolarScale)
export const Search = wrapIcon(SolarMagnifer)
export const Send = wrapIcon(SolarPlain) // (approx)
export const Settings = wrapIcon(SolarSettings)
export const Shield = wrapIcon(SolarShield)
export const ShieldCheck = wrapIcon(SolarShieldCheck)
export const SlidersHorizontal = wrapIcon(SolarTuning) // (approx)
export const Smile = wrapIcon(SolarSmileCircle)
export const Sparkles = wrapIcon(SolarStars) // (approx)
export const Structure = wrapIcon(SolarStructure as ComponentType<SolarIconProps>)
export const Sun = wrapIcon(SolarSun)
export const Tag = wrapIcon(SolarTag)
export const Theater = wrapIcon(SolarMasks) // (approx)
export const TimerReset = wrapIcon(SolarStopwatch) // (approx)
export const Trash2 = wrapIcon(SolarTrashBinMinimalistic)
export const Trophy = wrapIcon(SolarCup) // (approx)
export const Underline = wrapIcon(SolarTextUnderline)
export const Upload = wrapIcon(SolarUpload)
export const User = wrapIcon(SolarUser)
export const UserCheck = wrapIcon(SolarUserCheck)
export const UserMinus = wrapIcon(SolarUserMinus)
export const UserPlus = wrapIcon(SolarUserPlus)
export const Users = wrapIcon(SolarUsersGroupRounded)
export const UtensilsCrossed = wrapIcon(SolarChefHat)
export const Wrench = wrapIcon(SolarTuning2) // (approx)
export const X = wrapIcon(SolarCloseCircle) // (approx)
